import { createFileRoute } from "@tanstack/react-router";
import provinces from "@/constants/provinces.json";
import cities from "@/constants/cities.json";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getProfile, updateProfile } from "@/queries/profile";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import DatePicker from "@/components/ui/date-picker"; // Assuming shadcn date picker is in this path
import client from "@/services/api";
import userStore from "@/stores/user-store";
import { useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { useStore } from "@tanstack/react-store";
import { toast } from "sonner";
import { digitsFaToEn, digitsArToEn } from "@persian-tools/persian-tools";

export const Route = createFileRoute("/profile/update/")({
  component: UpdateProfile,
});

// Converts Persian and Arabic-indic digits to plain English digits.
const toEnglishDigits = (value: string) =>
  digitsArToEn(digitsFaToEn(value ?? ""));

const validationSchema = z.object({
  province: z.string().min(1, "وارد کردن استان الزامی می باشد"),
  city: z.string().min(1, "وارد کردن شهر الزامی می باشد"),
  address: z
    .string()
    .regex(
      /^[\u0600-\u06FF0-9_\-\s]+$/gm,
      "آدرس پستی باید به زبان فارسی نوشته شود",
    )
    .min(1, "وارد کردن آدرس الزامی می باشد"),
  postal_code: z
    .string()
    .regex(
      /(?!(\d)\1{3})[13-9]{4}[1346-9][013-9]{5}/gm,
      "کد پستی وارد شده صحیح نمی باشد",
    )
    .min(1, "وارد کردن کد پستی ضروری است"),
  national_code: z
    .string()
    .min(10, "کد ملی باید حداقل 10 رقم باشد")
    .max(10, "کد ملی نمی تواند بیشتر از 10 رقم باشد")
    .min(1, "وارد کردن کد ملی الزامی است"),
  store_name: z
    .string()
    .max(70, "نام فروشگاه نمی تواند بیشتر از ۷۰ کارکتر باشد")
    .min(1, "وارد کردن نام فروشگاه الزامی است"),
  gender: z.string().min(1, "انتخاب یک جنسیت ضروری می باشد"),
  birth_date: z.date().optional(),
  profession: z.string().optional(),
  education: z.string().optional(),
  phone: z.string().optional(),
});

type ValidationSchema = z.infer<typeof validationSchema>;

function UpdateProfile() {
  const navigate = Route.useNavigate();
  const { user } = useStore(userStore);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      province: "",
      city: "",
      address: "",
      postal_code: "",
      birth_date: undefined,
      profession: "",
      education: "",
      store_name: "",
      phone: "",
      national_code: "",
      gender: "",
    },
    mode: "onChange",
  });

  const provinceValue = provinces.filter(
    (p) => p.name === watch("province"),
  )?.[0]?.id;

  const currentProfile = useQuery({
    queryFn: () => getProfile(),
    queryKey: ["currentProfile"],
  });

  useEffect(() => {
    if (currentProfile.data) {
      const data: any = currentProfile.data.data.data;
      const cleaned = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value != null),
      );
      reset({ ...cleaned, birth_date: new Date(data?.birth_date) });
    }
  }, [currentProfile.data]);

  const profileUpdateMutation = useMutation({
    mutationFn: (variable: Object) => updateProfile(variable),
    mutationKey: ["updateProfile"],
    onSuccess: () => {
      toast.success("پروفایل با موفقیت ویرایش شد");
      client.get("/auth/check").then(({ data }: any) => {
        userStore.setState((prev) => ({
          ...prev,
          user: data.user,
          setting: data.setting.settings,
        }));
      });
      navigate({ to: "/" });
    },
    onError: () => {
      toast.error("خطایی در ویرایش اطلاعات رخ داده است");
    },
  });

  const onSubmit = (values: ValidationSchema) => {
    let data = { ...values } as any;
    const newData = Object.fromEntries(
      Object.entries(data).filter(([_, v]) => v !== "" && v !== undefined),
    );
    const profile: any = currentProfile?.data?.data.data;
    newData.national_code = profile.national_code;
    profileUpdateMutation.mutate({ ...newData, name: user?.name });
  };

  console.log(errors);

  // Register a text field but rewrite its value (and displayed content)
  // to English digits on every change, including paste.
  const registerDigitsField = (name: "postal_code" | "phone") => {
    const { onChange, ...rest } = register(name);
    return {
      ...rest,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value = toEnglishDigits(e.target.value);
        return onChange(e);
      },
    };
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>ویرایش پروفایل</CardTitle>
        </CardHeader>

        <CardContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="grid lg:grid-cols-4 gap-4"
          >
            <div className="mb-3">
              <Label>استان *</Label>
              <Combobox
                placeholder="استان خود را انتخاب کنید..."
                emptyMessage="استان مورد نظر یافت نشد"
                options={provinces.map((p: any) => ({
                  value: p.name,
                  label: p.name,
                }))}
                value={watch("province")}
                onSelect={(p: any) => setValue("province", p)}
              />
              <span className="text-xs text-red-500">
                {errors.province?.message}
              </span>
            </div>
            <div className="mb-3">
              <Label>شهر *</Label>
              <Combobox
                placeholder="شهر خود را انتخاب کنید"
                emptyMessage="شهر مورد نظر یافت نشد"
                options={cities
                  .filter((p: any) => p.province_id === provinceValue)
                  .map((p: any) => ({ value: p.name, label: p.name }))}
                value={watch("city")}
                onSelect={(v: any) => {
                  console.log(v);
                  setValue("city", v);
                }}
              />
              <span className="text-xs text-red-500">
                {errors.city?.message}
              </span>
            </div>
            <div className="flex flex-col gap-2 lg:col-span-2">
              <Label>آدرس</Label>
              <Textarea {...register("address")} />
              <span className="text-xs text-red-500">
                {errors.address?.message}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <Label>کد پستی</Label>
              <Input {...registerDigitsField("postal_code")} />
              <span className="text-xs text-red-500">
                {errors.postal_code?.message}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <Label>نام فروشگاه</Label>
              <Input {...register("store_name")} />
              <span className="text-xs text-red-500">
                {errors.store_name?.message}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <Label>کد ملی</Label>
              <Input {...register("national_code")} disabled />
              <span className="text-xs text-red-500">
                {errors.national_code?.message}
              </span>
            </div>

            <div className="mb-3 flex flex-col gap-2">
              <Label>جنسیت *</Label>
              <Combobox
                options={[
                  { label: "آقا", value: "male" },
                  { label: "خانم", value: "female" },
                ]}
                value={watch("gender")}
                onSelect={(e) => setValue("gender", e)}
              />
              <span className="text-xs text-red-500">
                {errors.gender?.message}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <Controller
                control={control}
                name="birth_date"
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    label="تاریخ تولد"
                  />
                )}
              />
              <span className="text-xs text-red-500">
                {errors.birth_date?.message}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <Label>شغل</Label>
              <Input {...register("profession")} />
              <span className="text-xs text-red-500">
                {errors.profession?.message}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <Label>تحصیلات</Label>
              <Input {...register("education")} />
              <span className="text-xs text-red-500">
                {errors.education?.message}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <Label>شماره تلفن ثابت</Label>
              <Input {...registerDigitsField("phone")} />
              <span className="text-xs text-red-500">
                {errors.phone?.message}
              </span>
            </div>

            <div className="lg:col-span-4">
              <Button
                color="btn-primary"
                type="submit"
                isLoading={profileUpdateMutation.isPending || isSubmitting}
                disabled={profileUpdateMutation.isPending || isSubmitting}
              >
                ویرایش اطلاعات
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
